import { ReactNode, useMemo, useState } from "react";
import type { BoxplotData, ChartData, ContinuousData, DimensionSeries } from "../config/data";
import type { Timeseries } from "../types";
import type { MeasureDef } from "../config/metrics";
import type { AxisFormatter } from "./charts/XmrChart";

interface ChartFrameProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  data: ChartData;
  chartLabel: string;
  measure?: MeasureDef;
  axisFormatter?: AxisFormatter;
  contextLabel?: string;
  selectedDate?: string | null;
  onDateSelect?: (date: string | null) => void;
  headerExtra?: ReactNode;
  /** Content shown at the top of the expanded "View data" section */
  detailsHeader?: ReactNode;
}

interface ChartDetailsProps {
  data: ChartData;
  chartLabel: string;
  measure?: MeasureDef;
  axisFormatter?: AxisFormatter;
  contextLabel?: string;
  selectedDate?: string | null;
  onClearDate?: () => void;
}

interface TableData {
  columns: string[];
  rows: string[][];
  sortableColumns?: number[]; // indices of columns that can be sorted
  defaultSortColumn?: number; // index of column to sort by default
  defaultSortDirection?: "asc" | "desc";
  sortByColumn?: Record<number, number>; // map display column index to the column index to use for sorting
  hiddenColumns?: number[]; // column indices to hide from display
}

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

export function ChartFrame({
  title,
  subtitle,
  children,
  data,
  chartLabel,
  measure,
  axisFormatter,
  contextLabel,
  selectedDate,
  onDateSelect,
  headerExtra,
  detailsHeader,
}: ChartFrameProps) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="chart-frame">
      <div className="chart-frame__header">
        <h2>
          {title}
          {subtitle && <span className="chart-frame__subtitle">{subtitle}</span>}
        </h2>
        <div className="chart-frame__header-actions">
          {headerExtra}
          <button
            type="button"
            className="chart-frame__toggle"
            onClick={() => setShowDetails(prev => !prev)}
            aria-expanded={showDetails}
          >
            {showDetails ? "Hide data" : "View data"}
          </button>
        </div>
      </div>
      <div className="chart-frame__body">{children}</div>
      {showDetails ? (
        <div className="chart-frame__details">
          {detailsHeader}
          <ChartDetails
            data={data}
            measure={measure}
            chartLabel={chartLabel}
            axisFormatter={axisFormatter}
            contextLabel={contextLabel}
            selectedDate={selectedDate}
            onClearDate={onDateSelect ? () => onDateSelect(null) : undefined}
          />
        </div>
      ) : null}
    </div>
  );
}

function ChartDetails({
  data,
  measure,
  chartLabel,
  axisFormatter,
  contextLabel,
  selectedDate,
  onClearDate,
}: ChartDetailsProps) {
  const table = useMemo(() => buildTableData(data, axisFormatter, selectedDate), [data, axisFormatter, selectedDate]);

  return (
    <div className="chart-details">
      <div className="chart-details__meta">
        <div>
          <strong>Chart type:</strong> {chartLabel}
        </div>
        {contextLabel ? (
          <div>
            <strong>Context:</strong> {contextLabel}
          </div>
        ) : null}
      </div>
      {selectedDate ? (
        <div className="chart-details__date-selection">
          <strong>Showing data for:</strong> {selectedDate}
          {onClearDate ? (
            <button type="button" onClick={onClearDate} className="chart-details__clear-btn">
              Show all dates
            </button>
          ) : null}
        </div>
      ) : null}
      {measure ? <MetricDefinition measure={measure} /> : null}
      {table ? <ChartDataTable table={table} /> : <p>No data to display.</p>}
    </div>
  );
}

function MetricDefinition({ measure }: { measure: MeasureDef }) {
  return (
    <div className="metric-definition">
      {measure.description ? <p className="metric-definition__description">{measure.description}</p> : null}
      <dl>
        <div>
          <dt>ID</dt>
          <dd>{measure.id}</dd>
        </div>
        <div>
          <dt>Owner</dt>
          <dd>{measure.owner ?? "todo:missing"}</dd>
        </div>
        <div>
          <dt>Aggregation</dt>
          <dd>{measure.agg}</dd>
        </div>
        <div>
          <dt>Unit</dt>
          <dd>{measure.unit ?? "n/a"}</dd>
        </div>
        <div>
          <dt>Format</dt>
          <dd>{measure.format ?? "n/a"}</dd>
        </div>
        <div>
          <dt>SQL</dt>
          <dd>
            <code>{measure.sql}</code>
          </dd>
        </div>
      </dl>
    </div>
  );
}

function ChartDataTable({ table }: { table: TableData }) {
  const [sortColumn, setSortColumn] = useState<number | undefined>(table.defaultSortColumn);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(table.defaultSortDirection ?? "asc");

  const hiddenSet = useMemo(() => new Set(table.hiddenColumns ?? []), [table.hiddenColumns]);

  const sortedRows = useMemo(() => {
    if (sortColumn === undefined || !table.sortableColumns?.includes(sortColumn)) {
      return table.rows;
    }

    // Use sortByColumn mapping if available, otherwise sort by the column itself
    const actualSortCol = table.sortByColumn?.[sortColumn] ?? sortColumn;

    return [...table.rows].sort((a, b) => {
      const aVal = a[actualSortCol];
      const bVal = b[actualSortCol];

      // Try numeric comparison first
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Fall back to string comparison
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [table.rows, table.sortableColumns, table.sortByColumn, sortColumn, sortDirection]);

  const handleSort = (colIdx: number) => {
    if (!table.sortableColumns?.includes(colIdx)) return;

    if (sortColumn === colIdx) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(colIdx);
      setSortDirection("desc");
    }
  };

  return (
    <div className="chart-data-table">
      <table>
        <thead>
          <tr>
            {table.columns.map((column, colIdx) => {
              if (hiddenSet.has(colIdx)) return null;
              const isSortable = table.sortableColumns?.includes(colIdx);
              const isSorted = sortColumn === colIdx;
              return (
                <th
                  key={column}
                  scope="col"
                  className={isSortable ? "sortable" : undefined}
                  onClick={isSortable ? () => handleSort(colIdx) : undefined}
                  aria-sort={isSorted ? sortDirection : undefined}
                >
                  {column}
                  {isSorted ? (sortDirection === "asc" ? " ▲" : " ▼") : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, rowIdx) => (
            <tr key={`${rowIdx}-${row[0]}`}>
              {row.map((cell, cellIdx) =>
                hiddenSet.has(cellIdx) ? null : <td key={`${rowIdx}-${cellIdx}`}>{cell}</td>,
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildTableData(
  data: ChartData,
  axisFormatter?: AxisFormatter,
  selectedDate?: string | null,
): TableData | null {
  if (isBoxplotData(data)) return buildBoxplotTable(data, axisFormatter, selectedDate);
  if (isContinuousData(data)) return buildContinuousTable(data, axisFormatter);
  if (isDimensionSeries(data)) return buildDimensionTable(data, axisFormatter);
  if (isTimeseries(data)) return buildTimeseriesTable(data, axisFormatter);
  return null;
}

function buildTimeseriesTable(timeseries: Timeseries, axisFormatter?: AxisFormatter): TableData {
  return {
    columns: ["Timestamp", "Value", "Raw"],
    rows: timeseries.timestamps.map((timestamp, idx) => [
      dateFormatter.format(timestamp),
      formatValue(timeseries.data[idx], axisFormatter),
      `${timeseries.data[idx]}`,
    ]),
  };
}

function buildContinuousTable(data: ContinuousData, axisFormatter?: AxisFormatter): TableData {
  const length = data.mean.timestamps.length;
  return {
    columns: ["Timestamp", "Mean", "Std dev", "Normalize by"],
    rows: Array.from({ length }, (_, idx) => {
      const timestamp = data.mean.timestamps[idx];
      const normalizeValue = data.normalizeBy.data[idx] ?? 0;
      const stdValue = data.std.data[idx] ?? 0;
      return [
        dateFormatter.format(timestamp),
        formatValue(data.mean.data[idx], axisFormatter),
        formatNumber(stdValue),
        formatNumber(normalizeValue),
      ];
    }),
  };
}

function buildDimensionTable(series: DimensionSeries, axisFormatter?: AxisFormatter): TableData {
  const rows: string[][] = [];
  for (const [dimension, timeseries] of Object.entries(series)) {
    for (let idx = 0; idx < timeseries.timestamps.length; idx++) {
      rows.push([
        dimension,
        dateFormatter.format(timeseries.timestamps[idx]),
        formatValue(timeseries.data[idx], axisFormatter),
        `${timeseries.data[idx]}`,
      ]);
    }
  }

  rows.sort((a, b) => a[1].localeCompare(b[1]));

  return {
    columns: ["Dimension", "Timestamp", "Value", "Raw"],
    rows,
  };
}

function isTimeseries(data: ChartData): data is Timeseries {
  return Boolean((data as Timeseries).timestamps) && !isBoxplotData(data);
}

function isContinuousData(data: ChartData): data is ContinuousData {
  return Boolean((data as ContinuousData).mean && (data as ContinuousData).std);
}

function isDimensionSeries(data: ChartData): data is DimensionSeries {
  return !isTimeseries(data) && !isContinuousData(data) && !isBoxplotData(data);
}

function isBoxplotData(data: ChartData): data is BoxplotData {
  return Boolean((data as BoxplotData).boxplotValues);
}

function buildBoxplotTable(data: BoxplotData, axisFormatter?: AxisFormatter, selectedDate?: string | null): TableData {
  // Use per-date data if a date is selected, otherwise use aggregate
  const values =
    selectedDate && data.dimensionValuesByDate?.[selectedDate]
      ? data.dimensionValuesByDate[selectedDate]
      : data.dimensionValues;

  if (!values || values.length === 0) {
    return { columns: ["Dimension", "Value", "Percentile", "_rank"], rows: [], hiddenColumns: [3] };
  }

  // Sort by value to calculate percentile ranks
  const sorted = [...values].sort((a, b) => a.value - b.value);
  const n = sorted.length;

  // Create maps from name to percentile and rank
  const percentileMap = new Map<string, number>();
  const rankMap = new Map<string, number>();
  for (let i = 0; i < n; i++) {
    // Percentile: percentage of values that are less than or equal to this value
    const percentile = ((i + 1) / n) * 100;
    percentileMap.set(sorted[i].name, percentile);
    // Rank: position in sorted order (used for stable sorting when values are equal)
    rankMap.set(sorted[i].name, i + 1);
  }

  const rows = values.map(item => [
    item.name,
    formatValue(item.value, axisFormatter),
    `${percentileMap.get(item.name)?.toFixed(0)}%`,
    `${rankMap.get(item.name)}`, // hidden rank for stable sorting
  ]);

  return {
    columns: ["Dimension", "Value", "Percentile", "_rank"],
    rows,
    sortableColumns: [0, 1, 2],
    defaultSortColumn: 1,
    defaultSortDirection: "desc",
    sortByColumn: { 1: 3, 2: 3 }, // sort Value and Percentile columns by _rank for stable ordering
    hiddenColumns: [3],
  };
}

function formatValue(value: number, axisFormatter?: AxisFormatter): string {
  if (!Number.isFinite(value)) return "n/a";
  try {
    return axisFormatter ? axisFormatter(value) : formatNumber(value);
  } catch {
    return formatNumber(value);
  }
}

function formatNumber(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 4 }) : "n/a";
}

export default ChartFrame;
